import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { clarityTokens } from '@verifime/design-tokens';
import { withClarityTheme } from '@verifime/styles';
import { ReactNode, useId } from 'react';

export type EntityTypeTileProps = {
  /**
   * The icon to display at the top of the tile
   */
  icon: ReactNode;
  /**
   * The title of the entity type (e.g., "Sole Trader", "Partnership")
   */
  title: string;
  /**
   * The main description of the entity type
   */
  description: string;
  /**
   * The hint text shown with an arrow (e.g., "Choose this if you're a freelancer...")
   */
  hint: string;
  /**
   * Whether this tile is currently selected
   */
  selected?: boolean;
  /**
   * Callback when the tile is clicked. Never called while `disabled`.
   */
  onClick?: () => void;
  /**
   * Whether the tile is visible but cannot be chosen. It is announced as disabled, ignores
   * clicks, and never shows as selected.
   */
  disabled?: boolean;
  /**
   * Optional data-testid for testing
   */
  dataTestId?: string;
};

/**
 * EntityTypeTile is a selectable card component for displaying entity types.
 * It shows an icon, title, description, and a hint with an arrow indicator.
 *
 * @example
 * ```tsx
 * <EntityTypeTile
 *   icon={<BriefcaseIcon />}
 *   title="Sole Trader"
 *   description="You run a business in your own name (or a business name) and you're personally responsible for it."
 *   hint="Choose this if you're a freelancer, contractor, or small business owner working for yourself."
 *   selected={selectedType === 'sole-trader'}
 *   onClick={() => setSelectedType('sole-trader')}
 * />
 * ```
 */
const EntityTypeTileComponent = ({
  icon,
  title,
  description,
  hint,
  selected = false,
  onClick,
  disabled = false,
  dataTestId,
}: Readonly<EntityTypeTileProps>) => {
  const theme = useTheme();
  const titleId = useId();
  const descriptionId = useId();
  const hintId = useId();

  // TODO: We need a immediately follow-up PR to refactor this component to use semantic colors instead of hardcoding clarity token colors.
  // This is a temporary solution to get the new entity type tiles out for the portal release, but we should migrate to semantic tokens for maintainability and theme consistency in the long run.
  const isDarkMode = theme.palette.mode === 'dark';
  // A disabled tile never looks selected, and it has no hover or click behaviour.
  const isSelected = selected && !disabled;
  const isInteractive = Boolean(onClick) && !disabled;
  const hoverBackground = isDarkMode
    ? clarityTokens.colorEggplant800
    : clarityTokens.colorEggplant100;
  const hintColour = isDarkMode ? clarityTokens.colorGrey300 : clarityTokens.colorGrey500;
  // Disabled swaps in the theme's disabled tokens. Typography sets its own colour, so the tile's
  // `color` does not reach the title and description: they take `palette.text` explicitly.
  const palette = disabled
    ? {
        background: theme.palette.action.disabledBackground,
        text: theme.palette.text.disabled,
        arrow: theme.palette.action.disabled,
        hint: theme.palette.text.disabled,
      }
    : {
        background: isSelected ? hoverBackground : theme.palette.background.paper,
        text: undefined,
        arrow: hintColour,
        hint: hintColour,
      };
  const selectedBorder = `${theme.semantic.spacing['3xs']} solid ${clarityTokens.colorEggplant500}`;
  const defaultBorder = `${theme.semantic.spacing['4xs']} solid ${
    isDarkMode ? clarityTokens.colorGrey700 : clarityTokens.colorGrey200
  }`;
  return (
    <ButtonBase
      data-testid={dataTestId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={isSelected}
      aria-labelledby={titleId}
      aria-describedby={`${descriptionId} ${hintId}`}
      sx={(theme) => ({
        // ButtonBase is inline-flex and centres its text; the tile is a left-aligned block card.
        width: '100%',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        textAlign: 'left',
        // ButtonBase blanks pointer events when disabled; the tile keeps its not-allowed cursor.
        // A disabled native button still fires no click.
        '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' },
        // Keyboard-only focus ring, same treatment as DesktopQrReveal: ButtonBase strips the
        // browser default, which would otherwise leave no visible focus indicator.
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: theme.semantic.spacing['3xs'],
        },
        padding: theme.semantic.spacing.lg,
        borderRadius: theme.semantic.borderRadius.sm,
        border: isSelected ? selectedBorder : defaultBorder,
        backgroundColor: palette.background,
        color: palette.text,
        transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
        '&:hover': isInteractive ? { backgroundColor: hoverBackground } : {},
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing.xs,
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
      })}
    >
      {/* Icon and Title. Spans, not divs: a button may only hold phrasing content. */}
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={(theme) => theme.semantic.spacing.xs}
      >
        {icon}
        <Typography variant="h6" component="span" id={titleId} sx={{ color: palette.text }}>
          {title}
        </Typography>
      </Stack>

      {/* Description */}
      <Typography variant="body2" component="span" id={descriptionId} sx={{ color: palette.text }}>
        {description}
      </Typography>

      {/* Hint with arrow */}
      <Stack component="span" direction="row" alignItems="flex-start">
        <ArrowForwardIcon sx={{ color: palette.arrow }} />
        <Typography variant="body2" component="span" id={hintId} sx={{ color: palette.hint }}>
          {hint}
        </Typography>
      </Stack>
    </ButtonBase>
  );
};

export const EntityTypeTile = withClarityTheme(EntityTypeTileComponent);
